/**
 * detectPrepState.js — is Detect's AI guidance HERE, COMING, or NOT COMING?
 *
 * ★★★ THE DEFECT THIS EXISTS FOR. Dr. Rami, walking Build 37: "I noticed in this
 * flow that the detect content was not ready by the time I arrived...can it
 * persist in a loading state until the content loads?"
 *
 * DetectScreen has retried for `detectPrep` since v220.9.3 — eight attempts over
 * ~70 s, because generation genuinely takes ~60 s. The retry was never the
 * problem. The problem is that all three prep zones render on `.length > 0`:
 *
 *     {!hasSubmittedDetect && prepCapturable.length   > 0 && ( ... )}
 *     {!hasSubmittedDetect && prepPriorRecords.length > 0 && ( ... )}
 *     {hasExamPreview                                     && ( ... )}
 *
 * An empty array means "not here". It does NOT mean "not coming" — and for the
 * first ~60 s of a case's life those are opposite facts wearing the same face.
 * The patient arrived at a bare uploader with no sign that anything was on its
 * way, so the screen told them the guidance did not exist. It existed; it was
 * eleven seconds out.
 *
 * ★★ THIS IS THE SAME COLLAPSE v242.95.0 JUST FIXED ONE SCREEN EARLIER, where
 *   ViewSummary's Submit CTA bailed on `dxCount === 0` and could not tell a case
 *   that found nothing from a case still looking. Same shape, same remedy: ask
 *   whether data is still COMING, not only whether any arrived. Two instances is
 *   a class, which is why the predicate is a module with a gate on it and not
 *   another inline `&&`.
 *
 * ★ A LOADING STATE MUST TERMINATE. "Persist until the content loads" cannot mean
 *   a spinner that outlives the fetch — the retry gives up after eight attempts,
 *   and when it does, the honest state is `unavailable` (fall back to the plain
 *   uploaders, exactly as before this change), NOT a skeleton that spins forever
 *   on a case where nothing is coming.
 */

/** The statuses in which a summary has been, or is being, built. */
export const SUMMARY_EXPECTED_STATUSES = [
  'shared', 'claimed', 'in_progress', 'scheduled', 'in_follow_up', 'in_observation', 'completed',
]

/**
 * Should this case have detect guidance at all?
 *
 * Mirrors the retry effect's own precondition (DetectScreen.jsx:329-333): if the
 * patient has not finished Describe and the case has not advanced, there is
 * genuinely nothing to build on, nothing is being generated, and a skeleton would
 * be a lie. Kept as one exported function so the screen and the retry cannot
 * drift apart — two declarations of one fact is how the 403 in REOPEN-PATIENT
 * happened.
 */
export function expectsDetectPrep(c) {
  if (!c) return false
  const describeDone = !!(c.describeComplete || c.describe_complete)
  const advanced = SUMMARY_EXPECTED_STATUSES.includes(c.status || '')
  return describeDone || advanced
}

/** Does this prep object actually carry anything renderable? */
export function hasDetectPrepContent(prep) {
  if (!prep || typeof prep !== 'object') return false
  const nonEmpty = (a) => Array.isArray(a) && a.length > 0
  // ★ v242.158.0 — `whatToExpect` NO LONGER COUNTS. Its card was removed from Detect
  //   (TestFlight 2026-09-06) while the field is still generated and stored. Leaving it
  //   here would let a case report "prep ready" on data that renders NOWHERE — a
  //   legal-looking value with no surface, which is 1dk(e). Content means content the
  //   patient can actually see.
  return nonEmpty(prep.capturable) || nonEmpty(prep.priorRecords)
}

/**
 * → 'ready' | 'pending' | 'unavailable'
 *
 *   ready        content is here; render it
 *   pending      we expect content and the retry is still running; render skeletons
 *   unavailable  nothing is coming (retry exhausted, or this case never had a
 *                summary to build on); render today's plain uploaders
 *
 * ★ `submitted` short-circuits to 'unavailable': the two capture zones already
 *   hide behind `!hasSubmittedDetect`, so a skeleton for them after submission
 *   would promise something that will never appear.
 */
export function detectPrepPhase({ prep, expects, retriesExhausted, submitted }) {
  if (hasDetectPrepContent(prep)) return 'ready'
  if (submitted) return 'unavailable'
  if (!expects) return 'unavailable'
  if (retriesExhausted) return 'unavailable'
  return 'pending'
}
